import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Megaphone } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const PUBLISHED = "2026-10-01";

const ARTICLE_LD = {
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline:
    "MAS Grading Brings Professional Card Grading On-Site to Collectors Nationwide — Convention Tour Trials Begin Q4 2026, National Rollout in 2027",
  description:
    "MAS Grading announces its On-Site Grading program, bringing witnessed, professional trading card grading directly to collectors at card shows and conventions across the United States.",
  image: ["https://grading.mascertified.com/og-image.jpg"],
  datePublished: PUBLISHED,
  dateModified: PUBLISHED,
  author: { "@type": "Organization", name: "MAS Grading", url: "https://grading.mascertified.com" },
  publisher: {
    "@type": "Organization",
    name: "MAS Grading",
    logo: { "@type": "ImageObject", url: "https://grading.mascertified.com/mas-logo-black.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": "https://grading.mascertified.com/press" },
};

const H2 = "font-display font-extrabold text-2xl md:text-3xl tracking-tight text-[#0A0A0A] pt-6";

export default function Press() {
  return (
    <div className="bg-white" data-testid="press-page">
      <Seo
        title="On-Site Grading Convention Tour — Press Release"
        description="MAS Grading brings witnessed, professional card grading on-site to conventions nationwide. Trial events begin Q4 2026, with a major national rollout in 2027."
        path="/press"
        type="article"
        jsonLd={ARTICLE_LD}
      />
      <PageHeader
        eyebrow="Newsroom"
        title="MAS Grading Hits the Road with On-Site Convention Grading"
        subtitle="Witnessed, professional card grading comes directly to collectors — trial events begin Q4 2026, with a major national rollout across U.S. conventions in 2027."
        word="PRESS"
      />
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-400 border-b border-neutral-200 pb-6" data-testid="press-dateline">
          <span className="inline-flex items-center gap-2"><Megaphone size={15} /> For Immediate Release</span>
          <span className="inline-flex items-center gap-2"><CalendarDays size={15} /> October 1, 2026</span>
          <span className="inline-flex items-center gap-2"><MapPin size={15} /> McAllen, Texas</span>
        </div>

        <div className="mt-8 space-y-6 text-neutral-600 leading-relaxed text-[17px]">
          <p>
            <strong className="text-[#0A0A0A]">MCALLEN, Texas — </strong>MAS Grading, the trading card grading division of
            MAS Certified, Inc., today announced the launch of its <strong className="text-[#0A0A0A]">On-Site Grading</strong> program —
            bringing fast, professional, witnessed card grading directly to collectors at card shows and conventions across the United
            States. Trial events begin in <strong className="text-[#0A0A0A]">Q4 2026</strong>, with a major national rollout planned
            for <strong className="text-[#0A0A0A]">2027</strong>.
          </p>

          <p>
            For decades, collectors have had essentially one option: mail their cards away and wait weeks — sometimes months — to get
            them back. MAS Grading's On-Site program changes that. At participating conventions, collectors can hand their cards to MAS
            specialists at the booth, follow the process in person, and in many cases walk away the same day with graded, tamper-evident
            MAS slabs in hand.
          </p>

          <h2 className={H2}>Grading you can actually watch</h2>
          <p>
            Every On-Site submission moves through a streamlined booth workflow: a self-serve iPad kiosk for intake, live fulfillment by
            trained MAS staff, on-demand slab labeling, and real-time SMS and email updates so collectors know the moment their cards are
            ready for pickup. It is grading made transparent — a natural extension of the <strong className="text-[#0A0A0A]">witnessed
            certification</strong> approach that built MAS Certified, Inc.'s reputation among collectors, dealers, and auction houses.
          </p>

          <h2 className={H2}>Meeting a multi-billion-dollar market where it gathers</h2>
          <p>
            Trading card grading has grown into a multi-billion-dollar global market, and the energy of the hobby lives at its shows and
            conventions. Rather than ask collectors to part with their most valuable cards and ship them into a black box, MAS Grading is
            meeting the community in person — on the convention floor, face to face, with the same standards and verifiable certification
            that define every MAS grade.
          </p>

          <blockquote className="border-l-2 border-[#0A0A0A] pl-5 italic text-[#0A0A0A]">
            "Collectors shouldn't have to mail away their most valuable cards and simply hope for the best," said a MAS Grading
            spokesperson. "We're bringing the grading room to the convention floor — transparent, fast, and witnessed from start to
            finish. Q4 2026 is just the beginning."
          </blockquote>

          <h2 className={H2}>Where to find MAS in 2026–2027</h2>
          <p>
            MAS Grading will announce its trial event schedule in the weeks ahead and expand to conventions nationwide through 2027.
            Collectors who want to be first to know can submit cards online today and verify any MAS certification instantly. Event
            organizers and media interested in hosting or covering MAS On-Site Grading can reach the team at
            {" "}<a className="font-semibold text-[#0A0A0A] underline underline-offset-4" href="mailto:support@mascertified.com">support@mascertified.com</a>.
          </p>

          <h2 className={H2}>About MAS Grading</h2>
          <p>
            MAS Grading is the trading card grading service from MAS Certified, Inc. It delivers precise, consistent grades,
            tamper-evident encapsulation, a live public population registry, and instant online certificate verification — built to
            protect and prove the value of collectors' cards. Learn more at grading.mascertified.com.
          </p>

          <p className="text-sm text-neutral-400 pt-2">
            Media contact: <a className="text-neutral-500 underline underline-offset-4" href="mailto:support@mascertified.com">support@mascertified.com</a>
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/submit" data-testid="press-cta-submit" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300">
            Submit an Item <ArrowRight size={16} />
          </Link>
          <Link to="/lookup" data-testid="press-cta-lookup" className="inline-flex items-center gap-2 px-8 py-4 border border-neutral-300 text-[#0A0A0A] font-bold rounded-full hover:border-[#0A0A0A] transition-colors duration-300">
            Verify a Cert
          </Link>
        </div>
      </article>
    </div>
  );
}
